import React, { useEffect, useRef, useState } from 'react';
import {
  BiChevronDown, BiChevronRight, BiFolder, BiFile, BiGridAlt,
} from 'react-icons/bi';

const findParentSectionId = (sectionId, sections) => {
  for (const section of sections || []) {
    if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
    const parentId = findParentSectionId(sectionId, section.subSections);
    if (parentId != null) return parentId;
  }
  return null;
};

const findSectionIdContainingField = (fieldId, sections) => {
  for (const section of sections || []) {
    if (section.fields?.some((f) => f.id === fieldId)) return section.id;
    const found = findSectionIdContainingField(fieldId, section.subSections);
    if (found != null) return found;
  }
  return null;
};

const PreviewTreeNavigation = ({
  formdata,
  activeSectionId,
  activeQuestionId,
  searchQuery,
  setSearchQuery,
  onSelectSection,
  onSelectSubSection,
  onSelectRootFields,
  onSelectQuestion,
}) => {
  const [expanded, setExpanded] = useState({ 'root-fields': true });
  const treeBodyRef = useRef(null);

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand ancestor sections/subsections of whichever item is currently active,
  // so the active Section/Subsection/Question stays visible in the tree.
  useEffect(() => {
    if (!formdata) return;

    let startSectionId = null;
    if (activeQuestionId != null) {
      startSectionId = findSectionIdContainingField(activeQuestionId, formdata.sections);
    } else if (activeSectionId != null && activeSectionId !== 0) {
      startSectionId = activeSectionId;
    }
    if (startSectionId == null) return;

    const ancestorIds = [];
    let currentId = startSectionId;
    while (currentId != null) {
      ancestorIds.push(currentId);
      currentId = findParentSectionId(currentId, formdata.sections);
    }

    setExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      ancestorIds.forEach((id) => {
        const key = `section-${id}`;
        if (next[key] === false) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [activeSectionId, activeQuestionId, formdata]);

  // Once the active Question/Section/Subsection's row is actually in the DOM
  // (which may require the auto-expand effect above to have run first, hence
  // depending on `expanded` too), bring it into view within the sidebar's own
  // scroll container — not the main Preview content. scrollIntoView's
  // 'nearest' block is a no-op if the row is already fully visible, so this
  // never scrolls when it doesn't need to.
  useEffect(() => {
    const container = treeBodyRef.current;
    if (!container) return;

    const selector = activeQuestionId != null
      ? `[data-tree-field-id="${activeQuestionId}"]`
      : activeSectionId != null
        ? `[data-tree-section-id="${activeSectionId}"]`
        : null;
    if (!selector) return;

    const target = container.querySelector(selector);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeSectionId, activeQuestionId, expanded]);

  const matchesSearch = (text) => {
    if (!searchQuery) return true;
    return text?.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const sectionMatchesSearch = (section) => {
    if (matchesSearch(section.title)) return true;
    if (section.fields?.some((f) => matchesSearch(f.name))) return true;
    return section.subSections?.some((sub) => sectionMatchesSearch(sub));
  };

  const renderFields = (fields, depth = 0, containerId = null, switchToContainer = null) => {
    if (!fields?.length) return null;

    const filtered = fields.filter((f) => matchesSearch(f.name) && !f.hide_alt);
    if (!filtered.length) return null;

    const isContainerActive = activeSectionId === containerId;

    return filtered.map((field) => {
      const isSelected = activeQuestionId === field.id;
      const handleClick = () => {
        if (!isContainerActive && switchToContainer) {
          switchToContainer();
        }
        onSelectQuestion?.(field.id);
      };

      return (
        <div
          key={field.id}
          role="treeitem"
          aria-selected={isSelected}
          data-tree-field-id={field.id}
          className={`builder-tree__item builder-tree__item--preview-field Preview-Question-field builder-tree__item--nested${depth > 0 ? '-2' : ''} ${isSelected ? 'builder-tree__item--selected' : ''}`}
          onClick={handleClick}
        >
          <BiFile className="builder-tree__icon" aria-hidden="true" />
          <span className="builder-tree__label">{field.name || 'Untitled'}</span>
        </div>
      );
    });
  };

  const renderSectionList = (sections, parentSubSections = null, depth = 0) => {
    if (!sections?.length) return null;

    const visible = sections.filter((s) => !s.hide_alt && sectionMatchesSearch(s));
    if (!visible.length) return null;

    return visible.map((section) => renderSection(section, depth, parentSubSections));
  };

  const renderSection = (section, depth = 0, parentSubSections = null) => {
    const key = `section-${section.id}`;
    const isExpanded = expanded[key] !== false;
    const isSelected = activeSectionId === section.id && !activeQuestionId;

    const handleSelect = () => {
      if (depth > 0 && parentSubSections) {
        onSelectSubSection(section.id, parentSubSections);
      } else {
        onSelectSection(section.id);
      }
    };

    return (
      <div key={section.id} role="group">
        <div className="builder-tree__section-row">
          <div
            role="treeitem"
            aria-expanded={isExpanded}
            aria-selected={isSelected}
            data-tree-section-id={section.id}
            className={`builder-tree__item ${depth > 0 ? 'builder-tree__item--nested' : ''} ${isSelected ? 'builder-tree__item--selected' : ''}`}
            onClick={handleSelect}
          >
            <button
              type="button"
              className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
              style={{ width: 24, height: 24, padding: 0 }}
              onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
            </button>
            <BiFolder className="builder-tree__icon" aria-hidden="true" />
            <span className="builder-tree__label">{section.title}</span>
            {/* <span className="builder-tree__count">{fieldCount}</span> */}
          </div>
        </div>
        {isExpanded && (
          <>
            {renderFields(section.fields, depth, section.id, handleSelect)}
            {renderSectionList(section.subSections, section.subSections, depth + 1)}
          </>
        )}
      </div>
    );
  };

  const renderRootFields = () => {
    if (!formdata?.fields?.length) return null;

    const key = 'root-fields';
    const isExpanded = expanded[key] !== false;
    const isSelected = (activeSectionId === 0 || activeSectionId == null) && !activeQuestionId;
    const visibleCount = formdata.fields.filter((f) => matchesSearch(f.name)).length;

    return (
      <div role="group">
        <div
          role="treeitem"
          aria-expanded={isExpanded}
          aria-selected={isSelected}
          data-tree-section-id={0}
          className={`builder-tree__item ${isSelected ? 'builder-tree__item--selected' : ''}`}
          onClick={onSelectRootFields}
        >
          <button
            type="button"
            className="builder-ui-btn builder-ui-btn--ghost builder-ui-btn--icon"
            style={{ width: 24, height: 24, padding: 0 }}
            onClick={(e) => { e.stopPropagation(); toggleExpand(key); }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <BiChevronDown /> : <BiChevronRight />}
          </button>
          <BiGridAlt className="builder-tree__icon" />
          <span className="builder-tree__label">Root Questions</span>
          {/* <span className="builder-tree__count">{formdata.fields.length}</span> */}
        </div>
        {isExpanded && visibleCount > 0 && renderFields(formdata.fields, 0, 0, onSelectRootFields)}
      </div>
    );
  };

  if (!formdata) {
    return (
      <div className="builder-tree builder-tree--preview">
        <div className="builder-tree__header">
          <div className="builder-tree__search">
            <input disabled placeholder="Loading..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <nav className="builder-tree builder-tree--preview" aria-label="Form structure">
      <div className="builder-tree__header">
        <div className="builder-tree__search">
          <input
            type="search"
            placeholder="Filter tree…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Filter navigation tree"
          />
        </div>
      </div>
      <div className="builder-tree__body" role="tree" ref={treeBodyRef}>
        {renderRootFields()}
        {renderSectionList(formdata.sections)}
      </div>
    </nav>
  );
};

export default PreviewTreeNavigation;
